/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\DssKeysTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import { ChevronLeft, ChevronRight, LayoutTemplate } from 'lucide-react';
import Combobox from '../../../components/ui/Combobox';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import { DssKeyConfig } from '../types';
import { extractDssKeysInfo } from '../utils/extractOptions';
import type * as api from '../api';

interface DssKeysTabProps {
  selectedModel: api.DeviceModel;
  dssKeys: DssKeyConfig[];
  setDssKeys: (updater: (prev: DssKeyConfig[]) => DssKeyConfig[]) => void;
  dssPage: number;
  setDssPage: (page: number | ((p: number) => number)) => void;
  dssPerPage: number;
  setDssPerPage: (n: number) => void;
  setValue: (field: 'keys', value: DssKeyConfig[]) => void;
}

export function DssKeysTab({
  selectedModel,
  dssKeys,
  setDssKeys,
  dssPage,
  setDssPage,
  dssPerPage,
  setDssPerPage,
  setValue,
}: DssKeysTabProps) {
  const { t } = useI18n();
  const dssInfo = extractDssKeysInfo(selectedModel.keys, selectedModel.accounts);

  if (dssInfo.count === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{t('provisioningTemplates.noDssKeys', 'This model does not support DSS Keys configuration.')}</p>
      </div>
    );
  }

  const totalItems = dssKeys.length;
  const totalPages = Math.ceil(totalItems / dssPerPage);
  const startIndex = (dssPage - 1) * dssPerPage;
  const endIndex = Math.min(startIndex + dssPerPage, totalItems);
  const currentPageKeys = dssKeys.slice(startIndex, endIndex);

  const lineOptions = Array.from({ length: dssInfo.maxAccounts }, (_, i) => ({
    value: `Line ${i + 1}`,
    label: `Line ${i + 1}`,
  }));

  const updateDssKey = (keyIndex: number, field: keyof DssKeyConfig, value: string) => {
    const absoluteIndex = startIndex + keyIndex;
    setDssKeys(prev => {
      const updated = [...prev];
      updated[absoluteIndex] = { ...updated[absoluteIndex], [field]: value };
      setValue('keys', updated);
      return updated;
    });
  };

  return (
    <FormBox title={t('provisioningTemplates.tabs.dss', 'DSS KEYS')} icon={<LayoutTemplate className="h-4 w-4" />}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <select
            value={dssPerPage}
            onChange={(e) => { setDssPerPage(Number(e.target.value)); setDssPage(1); }}
            className="px-2 py-1 border border-border rounded text-sm bg-background"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-muted-foreground">{t('common.itemsPerPage', 'entries per page')}</span>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-16">{t('provisioningTemplates.key', 'Keys')}</th>
              <th className="px-3 py-2 text-left font-medium w-60">{t('provisioningTemplates.type', 'Type')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('common.description', 'Description')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.value', 'Value')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.extension', 'Extension')}</th>
              <th className="px-3 py-2 text-left font-medium w-40">{t('provisioningTemplates.line', 'Line')}</th>
            </tr>
          </thead>
          <tbody>
            {currentPageKeys.map((key, idx) => (
              <tr key={key.keyNumber} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-3 py-2 text-muted-foreground font-medium">{key.keyNumber}</td>
                <td className="px-3 py-1">
                  <Combobox
                    single
                    options={dssInfo.types.map(type => ({
                      value: type.value,
                      label: type.label,
                      checked: key.type === type.value,
                    }))}
                    onToggle={(val) => updateDssKey(idx, 'type', val)}
                    placeholder={t('provisioningTemplates.selectType', 'Select type...')}
                    className="w-full"
                  />
                </td>
                <td className="px-3 py-1">
                  <input type="text" value={key.description} onChange={(e) => updateDssKey(idx, 'description', e.target.value)}
                    className="w-full px-2 py-1 border border-border rounded text-sm bg-background" />
                </td>
                <td className="px-3 py-1">
                  <input type="text" value={key.value} onChange={(e) => updateDssKey(idx, 'value', e.target.value)}
                    className="w-full px-2 py-1 border border-border rounded text-sm bg-background" />
                </td>
                <td className="px-3 py-1">
                  <input type="text" value={key.extension} onChange={(e) => updateDssKey(idx, 'extension', e.target.value)}
                    className="w-full px-2 py-1 border border-border rounded text-sm bg-background" />
                </td>
                <td className="px-3 py-1">
                  <Combobox
                    single
                    options={lineOptions.map(opt => ({ value: opt.value, label: opt.label, checked: key.line === opt.value }))}
                    onToggle={(val) => updateDssKey(idx, 'line', val)}
                    placeholder={t('provisioningTemplates.selectLine', 'Select line...')}
                    className="w-full"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground">
          {t('common.showing', 'Showing')} {startIndex + 1} {t('common.to', 'to')} {endIndex} {t('common.of', 'of')} {totalItems} {t('common.results', 'entries')}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setDssPage(1)} disabled={dssPage === 1}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
          <button type="button" onClick={() => setDssPage(p => Math.max(1, p - 1))} disabled={dssPage === 1}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">
            <ChevronLeft className="h-4 w-4" />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) pageNum = i + 1;
            else if (dssPage <= 3) pageNum = i + 1;
            else if (dssPage >= totalPages - 2) pageNum = totalPages - 4 + i;
            else pageNum = dssPage - 2 + i;
            return (
              <button key={pageNum} type="button" onClick={() => setDssPage(pageNum)}
                className={`px-3 py-1 text-sm border border-border rounded ${dssPage === pageNum ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                {pageNum}
              </button>
            );
          })}
          <button type="button" onClick={() => setDssPage(p => Math.min(totalPages, p + 1))} disabled={dssPage === totalPages}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setDssPage(totalPages)} disabled={dssPage === totalPages}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
        </div>
      </div>
    </FormBox>
  );
}
